import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { ModuleRegistry, AllCommunityModule, themeBalham } from 'ag-grid-community';

ModuleRegistry.registerModules([
    AllEnterpriseModule,
    AllCommunityModule
]);

const TAB_ITEMS = [
    { value: 'input', label: '投入数' },
    { value: 'order', label: '受注数' },
    { value: 'delivery', label: '納品予定' }
];
const MAX_BASIC_INFO_HINBAN_COUNT = 999;
const INPUT_QTY_MAX_LENGTH = 6;

const TAB_FIELDS = {
    order: [
        { key: 'SJC_TENJINO', label: '展示会No' },
        { key: 'TNJ_NAME', label: '展示会名称' },
        { key: 'SJCM_HINBAN', label: '品番' },
        { key: 'SJCM_COLORCD', label: 'カラー' },
        { key: 'SJCM_SIZECD', label: 'サイズ' },
        { key: 'SJCM_JYUCYUSU', label: '数量' },
        { key: 'SJCH_TOKUISAKICD', label: '店舗' },
        { key: 'SJCH_TORIKEITAISYOSAIKBN', label: 'チャネル' },
        { key: '_COLOR_TOTAL', label: 'カラー計' },
        { key: '_QTY_TOTAL', label: '数量合計' }
    ],
    delivery: [
        { key: 'PDT_SYOHINCD', label: '品番' },
        { key: 'SSDNY_NO', label: '加工指示番号' },
        { key: 'SSDNY_COLORCD', label: 'カラー' },
        { key: 'SSDNY_SIZECD', label: 'サイズ' },
        { key: 'TNYC_MDHATCHUSU', label: '投入数' },
        { key: 'SSRM_SIIREBI', label: '仕入実績日' },
        { key: 'SSRM_SIIRESU', label: '仕入実績数' },
        { key: 'SSDNY_NOUHINYOTEI', label: '納品予定日' },
        { key: 'SSDNY_NOUHINYOTEI_SU', label: '納品予定数' },
        { key: 'SSDNY_CENTER', label: 'センター納品日' },
        { key: 'SSDNY_CENTER_SU', label: 'センター納品数' },
        { key: '_REMAIN_QTY', label: '残数' }
    ]
};

const COLORS = {
    panelBg: 'var(--md-panel-bg, #efefef)',
    headBg: 'var(--md-head-bg, #8ea0a0)',
    headText: 'var(--md-head-fg, #fff)',
    line: '#d7d7d7',
    btn: 'var(--md-btn-bg, #4caf50)',
    btnText: '#fff',
    listEvenBg: '#f0f1fb'
};

const GRID_THEME_STYLE = {
    '--ag-header-background-color': COLORS.headBg,
    '--ag-header-foreground-color': COLORS.headText,
    '--ag-header-column-separator-display': 'none',
    '--ag-header-column-border': `1px solid ${COLORS.line}`,
    '--ag-header-column-border-height': '100%',
    '--ag-header-column-resize-handle-display': 'none',
    '--ag-odd-row-background-color': '#f0f1fb',
    '--ag-border-color': COLORS.line,
    '--ag-row-border-color': COLORS.line,
    '--ag-borders': 'solid 1px',
    '--ag-font-size': '11px',
    '--ag-grid-size': '4px'
};

const GRID_LOCALE_TEXT = {
    cut: '切り取り',
    paste: '貼り付け',
    copy: 'コピー',
    copyWithHeaders: 'ヘッダ付きコピー'
};
const NO_INPUT_FIELD_LABELS = new Set([
    'PS品番',
    'ブランド品番',
    'パターンNo',
    '販売シーズン',
    'アイテム',
    '上代',
    '仕入原価',
    '発注単価',
    '確定単価',
    '設定率',
    '原価率'
]);
const NO_INPUT_LEFT_INDEXES = new Set([0, 1, 3, 4, 5, 6]);
const NO_INPUT_RIGHT_INDEXES = new Set([1, 2, 3, 4, 6]);
const MD_FIELD_TYPE = {
    TEXT: 0,
    NUMERIC: 1,
    DATE: 2,
    TEXTAREA: 3,
    SELECT: 4,
    MASTERREF: 5,
    MASTER: 6,
    MASTERCODE: 7,
    MASTERASST: 8,
    TAG: 10,
    STICKY: 11,
    COLOR: 12,
    CHECK: 13,
    APPROVAL: 14,
    SPIN: 15
};

// 基本情報タブで使う項目定義を作る。表示/入力/検索可否をこの定義へ集約する。
function inputField(label, key, options = {}) {
    const dataType = options.dataType === undefined ? MD_FIELD_TYPE.TEXT : options.dataType;
    return {
        label,
        key,
        dataType,
        maxLength: options.maxLength || 0,
        decimalLength: options.decimalLength || 0,
        defaultKey: options.defaultKey || '',
        defaultValue: options.defaultValue === undefined ? '' : options.defaultValue,
        nameKey: options.nameKey || (dataType === MD_FIELD_TYPE.MASTER ? `${key}_nm` : ''),
        searchable: options.searchable === undefined ? dataType === MD_FIELD_TYPE.MASTER : Boolean(options.searchable),
        required: Boolean(options.required),
        readOnly: Boolean(options.readOnly)
    };
}

const INPUT_LEFT_FIELD_DEFS = [
    inputField('PS品番', 'TNYM_OppiCd', { dataType: MD_FIELD_TYPE.TEXT, defaultKey: 'PDT_OppiCd', readOnly: true }),
    inputField('ブランド品番', 'TNYM_Hinban', { dataType: MD_FIELD_TYPE.TEXT, defaultKey: 'PDT_SyohinCD', readOnly: true }),
    inputField('先方品番', 'PDT_MakerHinban', { dataType: MD_FIELD_TYPE.TEXT, readOnly: true }),
    inputField('パターンNo', 'PDT_PatternNo1', { dataType: MD_FIELD_TYPE.TEXT, readOnly: true }),
    inputField('販売シーズン', 'PDT_HbSeasonCd', { dataType: MD_FIELD_TYPE.MASTER, nameKey: 'PDT_HbSeasonCd_nm', searchable: false, readOnly: true }),
    inputField('アイテム', 'TNYM_ITMCd', { dataType: MD_FIELD_TYPE.MASTER, nameKey: 'TNYM_ITMCd_nm', defaultKey: 'PDT_ITMCItemCd', searchable: false, readOnly: true }),
    inputField('上代', 'PDT_Price', { dataType: MD_FIELD_TYPE.NUMERIC, readOnly: true }),
    inputField('販売開始日', 'TNYM_EigyoNouki', { dataType: MD_FIELD_TYPE.DATE, maxLength: 8, defaultKey: 'PDTMD_SHOPYMD', required: true, readOnly: false }),
    inputField('販売終了日', 'TNYM_HanbaiEdDate', { dataType: MD_FIELD_TYPE.DATE, maxLength: 8 }),
    inputField('物流納期', 'TNYM_ButuryuNouki', { dataType: MD_FIELD_TYPE.DATE, maxLength: 8, defaultKey: 'SSD_BUTURYUNOUKI', required: true, readOnly: false }),
    inputField('生産納期', 'SSD_SeisanNouki', { dataType: MD_FIELD_TYPE.DATE, maxLength: 8, defaultKey: 'TNYM_ButuryuNouki', readOnly: true }),
    inputField('デザイナー', 'PDT_ChiefDesigner', { dataType: MD_FIELD_TYPE.TEXT }),
    inputField('仕入先', 'TNYM_SiireSakiCd', { dataType: MD_FIELD_TYPE.MASTER, nameKey: 'TNYM_SiireSakiCd_nm', defaultKey: 'SSD_SIIRESUBCD', required: true, readOnly: false }),
    inputField('加工先', 'TNYM_KakouSakiSubCd', { dataType: MD_FIELD_TYPE.MASTER, nameKey: 'TNYM_KakouSakiCd_nm' }),
    inputField('海外区分', 'TNYM_KaigaiKbn', { dataType: MD_FIELD_TYPE.MASTERREF, defaultKey: 'TOR_KaigaiKbn', readOnly: true }),
    inputField('発注形態', 'TNYM_HatsuKeitai', { dataType: MD_FIELD_TYPE.MASTERREF, defaultKey: 'PDT_HatsuKeitai', readOnly: true })
];

const INPUT_RIGHT_FIELD_DEFS = [
    inputField('仮原価', 'TNYM_KariGenka', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, defaultKey: 'PDT_KariGenka', readOnly: true }),
    inputField('仕入原価', 'TNYM_SeiChotatuTanka', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, readOnly: true }),
    inputField('発注単価', 'TNYM_SeiHatchuTanka', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, readOnly: true }),
    inputField('確定単価', 'TNYM_SeikakuteiTanka', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, readOnly: true }),
    inputField('設定率', '_SETTING_RATE', { dataType: MD_FIELD_TYPE.NUMERIC, readOnly: true }),
    inputField('投入理由', 'TNYM_TonyuRiyuKbn', { dataType: MD_FIELD_TYPE.TEXT, maxLength: 1, defaultValue: '1', required: true, readOnly: false }),
    inputField('原価率', '_COST_RATE', { dataType: MD_FIELD_TYPE.NUMERIC, readOnly: true }),
    inputField('要尺A', 'TNYM_YojakuA', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, decimalLength: 2 }),
    inputField('要尺B', 'TNYM_YojakuB', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, decimalLength: 2 }),
    inputField('要尺C', 'TNYM_YojakuC', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, decimalLength: 2 }),
    inputField('要尺D', 'TNYM_YojakuD', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, decimalLength: 2 })
];

const INPUT_BOTTOM_FIELD_DEFS = [
    inputField('加工指示No', 'SSD_No', { dataType: MD_FIELD_TYPE.TEXT, readOnly: true }),
    inputField('実MD単価', 'TNYM_MDHatchuTanka', { dataType: MD_FIELD_TYPE.NUMERIC, maxLength: 7, readOnly: true }),
    inputField('ロット換算', 'SSD_RotRound', { dataType: MD_FIELD_TYPE.NUMERIC, readOnly: true }),
    inputField('通貨単位', 'TNYM_SHD_Cur', { dataType: MD_FIELD_TYPE.MASTER, nameKey: 'TNYM_SHD_Cur_nm', defaultKey: 'TOR_TukaCd', searchable: false, readOnly: true }),
    inputField('発注状態', 'SHD_SynFlg1', { dataType: MD_FIELD_TYPE.TEXT, maxLength: 1 }),
    inputField('レート', 'TNYM_Rate', { dataType: MD_FIELD_TYPE.NUMERIC, defaultKey: 'CUR_Rate', readOnly: true }),
    inputField('中止', 'TNYM_StopFlg', { dataType: MD_FIELD_TYPE.CHECK }),
    inputField('確定', 'SSD_KanryoFlg', { dataType: MD_FIELD_TYPE.CHECK }),
    inputField('製商品発注', '_ORDER_TARGET', { dataType: MD_FIELD_TYPE.CHECK }),
    inputField('副資材登録', '_SUB_MATERIAL_TARGET', { dataType: MD_FIELD_TYPE.CHECK })
];

const INPUT_ALL_FIELD_DEFS = [
    ...INPUT_LEFT_FIELD_DEFS,
    ...INPUT_RIGHT_FIELD_DEFS,
    ...INPUT_BOTTOM_FIELD_DEFS
];

// ブランドはデータ種別6として扱い、検索アイコンを出さず「コード:名称」で表示する。
const INPUT_BRAND_FIELD_DEF = inputField('ブランド', 'PDT_BrdCode', {
    dataType: MD_FIELD_TYPE.MASTER,
    nameKey: 'PDT_BrdCode_nm',
    searchable: false,
    readOnly: true
});

// 画面の必須表示と登録前チェックで同じ定義を共有する。
export const MD_INPUT_REQUIRED_FIELD_DEFS = INPUT_ALL_FIELD_DEFS
    .filter((fieldDef) => fieldDef.required)
    .map((fieldDef) => ({ label: fieldDef.label, key: fieldDef.key }));

// 親画面が正規化した基本情報3タブデータだけを受け取る。
function resolveData(tabBaseInfo) {
    if (!tabBaseInfo || typeof tabBaseInfo !== 'object') return { input: {}, order: {}, delivery: {} };
    return {
        input: tabBaseInfo.input || {},
        order: tabBaseInfo.order || {},
        delivery: tabBaseInfo.delivery || {}
    };
}

// null/空を吸収して安全に文字列値を取り出す。
function valueOf(data, key, fallback = '') {
    if (!data) return fallback;
    const value = data[key];
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
}

// データ種別6の項目を「コード:名称」で表示する。名称が無い場合はコードのみ表示する。
function masterDisplayText(data, fieldDef) {
    if (!fieldDef) return '';
    const code = valueOf(data, fieldDef.key);
    if (fieldDef.dataType !== MD_FIELD_TYPE.MASTER) return code;
    const name = fieldDef.nameKey ? valueOf(data, fieldDef.nameKey) : '';
    if (code === '') return '';
    if (name === '' || name === code) return code;
    return `${code}:${name}`;
}

// 共通検索リストの戻り値から、最初に選択された行データだけを取り出す。
function pickSearchRow(eventData) {
    if (!eventData) return null;
    const items = Array.isArray(eventData.items) ? eventData.items : [];
    if (items.length === 0) return null;
    const first = items[0];
    if (!first) return null;
    return first.data || first;
}

// 投入数タブ右側の絵型画像。APIから返る表示用URLをそのまま表示する。
const InputBlockImage = React.memo(function ({ imageUrl, picturePath }) {
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        // 画像URL変更時はエラーフラグを初期化して再読込可能にする
        setImgError(false);
    }, [imageUrl]);

    return (
        <Box
            style={{
                height: '100%',
                border: `1px solid ${COLORS.line}`,
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }}
        >
            {!imageUrl || imgError ? (
                // 画像未取得/読込失敗時は代替表示
                <Typography variant="caption" style={{ color: '#666', textAlign: 'center', padding: 8 }}>
                    {picturePath === '' ? '画像' : `画像パス: ${picturePath}`}
                </Typography>
            ) : (
                <img
                    src={imageUrl}
                    alt="product"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#fff' }}
                    // 画像エラー時は代替表示へ切り替え
                    onError={() => setImgError(true)}
                />
            )}
        </Box>
    );
});

// GRID標準に近い見た目で、編集不可の値を表示する。
function ReadonlyInput({ value = '', align = 'left', height = 28 }) {
    return (
        <TextField
            size="small"
            variant="outlined"
            value={value}
            InputProps={{
                readOnly: true,
                style: { height: height, fontSize: 12 },
                inputProps: { style: { textAlign: align } }
            }}
            fullWidth
        />
    );
}
// 入力可能項目用の共通TextField。値の整形は呼び出し元のfieldDefに委ねる。
function EditableInput({ value = '', align = 'left', height = 28, onChange, inputProps = {} }) {
    return (
        <TextField
            size="small"
            variant="outlined"
            value={value}
            onChange={(event) => onChange && onChange(event.target.value)}
            InputProps={{
                style: { height: height, fontSize: 12 },
                inputProps: { ...inputProps, style: { textAlign: align, ...((inputProps && inputProps.style) || {}) } }
            }}
            fullWidth
        />
    );
}
// 基本情報の左見出しセル。通常GRIDのヘッダ色と合わせる。
function LabelCell({ children, width = 88 }) {
    return (
        <TableCell
            style={{
                backgroundColor: COLORS.headBg,
                color: COLORS.headText,
                width,
                fontWeight: 700,
                padding: '4px 8px',
                borderColor: COLORS.line,
                whiteSpace: 'nowrap'
            }}
        >
            {children}
        </TableCell>
    );
}

// ラベル + 入力/表示欄の1行を描画する。検索可能なMASTER項目だけ右矢印を出す。
function FieldRow({ label, value, displayValue, labelWidth, noInput = false, fieldDef = null, onChange, onSearch }) {
    const inputProps = getInputProps(fieldDef);
    const isMaster = fieldDef && fieldDef.dataType === MD_FIELD_TYPE.MASTER;
    const required = Boolean(fieldDef && fieldDef.required);
    // 編集不可/検索不要項目はアイコンを出さず、検索リストを起動させない。
    const showSearchIcon = !noInput && isMaster && fieldDef.searchable && onSearch;
    const shownValue = displayValue === undefined ? value : displayValue;
    const inputNode = noInput ? (
        <Box style={{ height: 28, padding: '0 8px', display: 'flex', alignItems: 'center', fontSize: 12, overflow: 'hidden' }}>
            {normalizeText(shownValue)}
        </Box>
    ) : isMaster ? (
        <ReadonlyInput value={shownValue} height={28} />
    ) : (
        <EditableInput
            value={value}
            onChange={(nextValue) => onChange && onChange(normalizeFieldInputValue(fieldDef, nextValue))}
            inputProps={inputProps}
        />
    );

    return (
        <TableRow style={{ height: 36 }}>
            <LabelCell width={labelWidth}>
                <Box display="flex" alignItems="center">
                    <span>{label}</span>
                    {required ? (
                        <Typography variant="caption" color="error" style={{ marginLeft: 4 }}>*</Typography>
                    ) : null}
                </Box>
            </LabelCell>
            <TableCell style={{ padding: 4, borderColor: COLORS.line }}>
                <Box display="flex" alignItems="center" style={{ gap: 2 }}>
                    <Box style={{ flex: 1, minWidth: 0 }}>{inputNode}</Box>
                    {showSearchIcon ? (
                        <IconButton
                            size="small"
                            onClick={() => onSearch(fieldDef)}
                            style={{ padding: 2, width: 24, height: 24 }}
                            title={`${label}を検索`}
                        >
                            <Icon style={{ fontSize: 18 }}>arrow_right</Icon>
                        </IconButton>
                    ) : null}
                </Box>
            </TableCell>
        </TableRow>
    );
}

// fieldDefからHTML入力属性を生成する。文字数/数値制限はここでまとめて付与する。
function getInputProps(fieldDef) {
    if (!fieldDef) return {};
    if (fieldDef.dataType === MD_FIELD_TYPE.TEXT && fieldDef.maxLength > 0) {
        return { maxLength: fieldDef.maxLength };
    }
    if (fieldDef.dataType === MD_FIELD_TYPE.NUMERIC && fieldDef.maxLength > 0) {
        const extraDot = fieldDef.decimalLength > 0 ? 1 : 0;
        return { maxLength: fieldDef.maxLength + extraDot, inputMode: 'decimal' };
    }
    return {};
}

// 全角数字/記号を半角へ寄せ、数値項目の入力揺れを抑える。
function normalizeHalfWidthNumber(value) {
    return String(value === undefined || value === null ? '' : value)
        .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
        .replace(/[．。]/g, '.')
        .replace(/，/g, ',');
}

// 数値入力を桁数・小数桁数に合わせて正規化する。
function normalizeNumericText(value, maxLength = 0, decimalLength = 0) {
    let text = normalizeHalfWidthNumber(value).trim().replace(/,/g, '');
    if (text === '') return '';

    let result = '';
    let dotUsed = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch >= '0' && ch <= '9') {
            result += ch;
        } else if (ch === '.') {
            // 小数0桁の項目は小数点以降を取り込まない。
            // 例: DB取得値 "1.0000" は "1" として扱う。
            if (decimalLength <= 0) break;
            if (!dotUsed) {
                result += ch;
                dotUsed = true;
            }
        }
    }
    if (result === '' || result === '.') return '';

    const parts = result.split('.');
    const integerLimit = maxLength > 0 ? Math.max(maxLength - decimalLength, 1) : 0;
    let integerPart = parts[0] || '';
    let decimalPart = parts.length > 1 ? parts.slice(1).join('') : null;

    integerPart = integerPart.replace(/^0+(?=\d)/, '');
    if (integerLimit > 0 && integerPart.length > integerLimit) integerPart = integerPart.substring(0, integerLimit);
    if (integerPart === '' && decimalPart !== null) integerPart = '0';

    // 小数あり項目は指定桁数までを保存し、それ以外は整数のみ保存する。
    if (decimalPart !== null && decimalLength > 0) {
        decimalPart = decimalPart.substring(0, decimalLength);
        return integerPart + (decimalPart.length !== 0 ? '.' + decimalPart : '');
    }
    return integerPart;
}

// 項目定義ごとに入力値を保存用の文字列へ正規化する。
function normalizeFieldInputValue(fieldDef, value) {
    if (!fieldDef) return value;
    if (fieldDef.dataType === MD_FIELD_TYPE.NUMERIC) {
        return normalizeNumericText(value, fieldDef.maxLength, fieldDef.decimalLength);
    }
    if (fieldDef.dataType === MD_FIELD_TYPE.TEXT && fieldDef.maxLength > 0) {
        return normalizeText(value).substring(0, fieldDef.maxLength);
    }
    return value;
}

// 投入数量は画面定義どおり整数6桁へ正規化する。
function normalizeInputQtyText(value) {
    return normalizeNumericText(value, INPUT_QTY_MAX_LENGTH, 0);
}

// null/undefined/空文字を吸収して、表示用文字列を安定させる。
function normalizeText(value, fallback = '') {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    if (text === '') return fallback;
    return text;
}

// 行データに項目値が無い場合、品番マスタ等から渡された初期値を補完する。
function getDefaultFieldValue(row, fieldDef) {
    if (!fieldDef) return '';
    if (fieldDef.defaultValue !== '') return String(fieldDef.defaultValue);
    const keys = Array.isArray(fieldDef.defaultKey) ? fieldDef.defaultKey : [fieldDef.defaultKey];
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (!key) continue;
        const value = normalizeText(row ? row[key] : '');
        if (value !== '') return value;
    }
    return '';
}

// 投入ブロック全体に項目定義の初期値を適用する。
function applyInputFieldDefaults(block) {
    const next = { ...((block && typeof block === 'object') ? block : {}) };
    INPUT_ALL_FIELD_DEFS.forEach((fieldDef) => {
        if (!fieldDef.key) return;
        if (normalizeText(next[fieldDef.key]) !== '') return;
        const defaultValue = getDefaultFieldValue(next, fieldDef);
        if (defaultValue !== '') next[fieldDef.key] = normalizeFieldInputValue(fieldDef, defaultValue);
    });
    return next;
}

// APIからJSON文字列で返る配列項目を安全に配列へ戻す。
function parseJsonArray(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed;
        } catch {
            return [];
        }
    }
    return [];
}

// 受注数タブのブロックJSONを配列に変換する。
function parseOrderBlocks(orderData) {
    if (!orderData) return [];
    return parseJsonArray(orderData._ORDER_BLOCKS);
}

// 受注/納品予定の明細行から数量合計を算出する。
function calcQty(rows) {
    let total = 0;
    let hasNumber = false;
    rows.forEach((row) => {
        const raw = normalizeText(row.qty).replace(/,/g, '');
        const value = Number(raw);
        if (!Number.isNaN(value)) {
            total += value;
            hasNumber = true;
        }
    });
    return hasNumber ? String(total) : '';
}

// サイズ/カラーなど、JSON配列またはカンマ区切り文字列を配列へ変換する。
function parseTextArray(value) {
    if (Array.isArray(value)) {
        return value.map((item) => normalizeText(item)).filter((item) => item !== '');
    }
    if (value === null || value === undefined) return [];

    const text = String(value).trim();
    if (text === '') return [];

    if (text.startsWith('[')) {
        return parseJsonArray(text).map((item) => normalizeText(item)).filter((item) => item !== '');
    }
    return text.split(',').map((item) => item.trim()).filter((item) => item !== '');
}

// ブロック未返却時は旧項目から推測せず、新規フォームと同じ空ブロックを表示する。
function buildEmptyOrderBlocks() {
    return [{
        hinban: '',
        groups: [],
        total: ''
    }];
}

// 受注数ブロックをUI表示向けに正規化する。
function normalizeOrderBlocks(orderData) {
    const rawBlocks = parseOrderBlocks(orderData);
    if (rawBlocks.length === 0) return buildEmptyOrderBlocks();

    return rawBlocks.map((block) => {
        const rawGroups = Array.isArray(block.groups) ? block.groups : [];
        const groups = rawGroups.map((group) => {
            const rawRows = Array.isArray(group.rows) ? group.rows : [];
            const rows = rawRows.map((row) => ({
                size: normalizeText(row.size),
                qty: normalizeText(row.qty),
                store: normalizeText(row.store, '-'),
                channel: normalizeText(row.channel, '-')
            }));
            return {
                color: normalizeText(group.color),
                rows: rows,
                total: normalizeText(group.total, calcQty(rows))
            };
        });
        const groupTotal = groups.reduce((sum, group) => {
            const num = Number(normalizeText(group.total).replace(/,/g, ''));
            return Number.isNaN(num) ? sum : sum + num;
        }, 0);
        const defaultTotal = groups.length > 0 ? String(groupTotal) : '';
        return {
            hinban: normalizeText(block.hinban, valueOf(orderData, 'SJCM_HINBAN')),
            groups: groups,
            total: normalizeText(block.total, defaultTotal)
        };
    }).slice(0, MAX_BASIC_INFO_HINBAN_COUNT);
}

// 納品予定タブのブロックJSONを配列に変換する。
function parseDeliveryBlocks(deliveryData) {
    if (!deliveryData) return [];
    return parseJsonArray(deliveryData._DELIVERY_BLOCKS);
}

// 集計用に文字列を数値化する。空/非数値はNaNとして呼び出し元で無視する。
function toNumber(value) {
    const text = normalizeText(value).replace(/,/g, '');
    if (text === '') return NaN;
    const num = Number(text);
    if (Number.isNaN(num)) return NaN;
    return num;
}

// 指定列の数量だけを合計する。値が1件も無い場合は空表示にする。
function sumByRows(rows, key) {
    let total = 0;
    let hasValue = false;
    rows.forEach((row) => {
        const num = toNumber(row[key]);
        if (!Number.isNaN(num)) {
            total += num;
            hasValue = true;
        }
    });
    return hasValue ? String(total) : '';
}

// 納品予定行の各数量合計を算出する。
function makeDeliveryTotals(rows) {
    return {
        inputQty: sumByRows(rows, 'inputQty'),
        actualQty: sumByRows(rows, 'actualQty'),
        planQty: sumByRows(rows, 'planQty'),
        centerQty: sumByRows(rows, 'centerQty'),
        remainQty: sumByRows(rows, 'remainQty')
    };
}

// ブロック未返却時は旧項目から推測せず、新規フォームと同じ空ブロックを表示する。
function buildEmptyDeliveryBlocks() {
    const rows = [];
    return [{
        hinban: '',
        processNo: '',
        actualDate: '',
        planDate: '',
        centerDate: '',
        rows: rows,
        totals: makeDeliveryTotals(rows)
    }];
}

// 納品予定ブロックをUI表示向けに正規化する。
function normalizeDeliveryBlocks(deliveryData) {
    const rawBlocks = parseDeliveryBlocks(deliveryData);
    if (rawBlocks.length === 0) return buildEmptyDeliveryBlocks();

    return rawBlocks.map((block) => {
        const rawRows = Array.isArray(block.rows) ? block.rows : [];
        const rows = rawRows.map((row) => ({
            color: normalizeText(row.color),
            size: normalizeText(row.size),
            inputQty: normalizeText(row.inputQty),
            actualQty: normalizeText(row.actualQty),
            planQty: normalizeText(row.planQty),
            centerQty: normalizeText(row.centerQty),
            remainQty: normalizeText(row.remainQty)
        }))
            // カラー/サイズ未設定の明細は表示対象から除外する。
            .filter((row) => row.color !== '' && row.size !== '');
        const totals = makeDeliveryTotals(rows);

        return {
            hinban: normalizeText(block.hinban, valueOf(deliveryData, 'PDT_SYOHINCD')),
            processNo: normalizeText(block.processNo, valueOf(deliveryData, 'SSDNY_NO')),
            actualDate: normalizeText(block.actualDate),
            planDate: normalizeText(block.planDate),
            centerDate: normalizeText(block.centerDate),
            rows: rows,
            totals: {
                inputQty: normalizeText(totals.inputQty),
                actualQty: normalizeText(totals.actualQty),
                planQty: normalizeText(totals.planQty),
                centerQty: normalizeText(totals.centerQty),
                remainQty: normalizeText(totals.remainQty)
            }
        };
    }).slice(0, MAX_BASIC_INFO_HINBAN_COUNT);
}

// 投入数タブのブロックJSONを配列に変換する。
function parseInputBlocks(inputData) {
    if (!inputData) return [];
    return parseJsonArray(inputData._INPUT_BLOCKS);
}

// 投入数タブのカラーサイズ明細行JSONを配列に変換する。
function parseInputQtyRows(inputData) {
    if (!inputData) return [];
    return parseJsonArray(inputData._QTY_ROWS);
}

// カラーサイズ表にデータが無い場合でも、画面上は6行分の入力枠を表示する。
function buildDefaultInputQtyRows() {
    return {
        sizes: [],
        rows: [
            { color: '', values: {}, total: '' },
            { color: '', values: {}, total: '' },
            { color: '', values: {}, total: '' },
            { color: '', values: {}, total: '' },
            { color: '', values: {}, total: '' },
            { color: '', values: {}, total: '' }
        ],
        totals: {}
    };
}

// 表示行数が不足している場合は空行を追加し、表の高さを安定させる。
function ensureMinInputQtyRows(rows, minRows = 6) {
    const list = Array.isArray(rows) ? [...rows] : [];
    while (list.length < minRows) {
        list.push({ color: '', values: {}, total: '' });
    }
    return list;
}

// 投入数タブのサイズ別合計JSONを配列に変換する。
function parseInputQtyTotals(inputData) {
    if (!inputData) return [];
    return parseJsonArray(inputData._QTY_TOTALS);
}

// 投入数量の集計結果は小数を持たない整数で表示する。
function formatQtyNumber(num) {
    if (Number.isNaN(num)) return '';
    return String(Math.trunc(num));
}

// 数量セルの文字列配列を合計し、空だけなら空文字を返す。
function sumQtyTexts(values) {
    let total = 0;
    let hasValue = false;
    values.forEach((val) => {
        const num = toNumber(val);
        if (!Number.isNaN(num)) {
            total += num;
            hasValue = true;
        }
    });
    if (!hasValue) return '';
    return formatQtyNumber(total);
}

// カラー/サイズ行列をサイズ軸・合計付きで整形する。
function normalizeInputQtyMatrix(inputData) {
    const rows = parseInputQtyRows(inputData);
    if (rows.length === 0) return buildDefaultInputQtyRows();

    const declaredSizes = parseTextArray(inputData ? inputData._QTY_SIZES : null);
    const sizes = [];
    declaredSizes.forEach((size) => {
        if (size !== '' && sizes.indexOf(size) === -1) sizes.push(size);
    });

    const normalizedRows = rows.map((row) => {
        const values = {};
        if (Array.isArray(row.values)) {
            row.values.forEach((item) => {
                const size = normalizeText(item ? item.size : '');
                if (size === '') return;
                const qty = normalizeInputQtyText(item ? item.qty : '');
                values[size] = qty;
                if (sizes.indexOf(size) === -1) sizes.push(size);
            });
        } else if (row.values && typeof row.values === 'object') {
            Object.keys(row.values).forEach((key) => {
                const size = normalizeText(key);
                if (size === '') return;
                const qty = normalizeInputQtyText(row.values[key]);
                values[size] = qty;
                if (sizes.indexOf(size) === -1) sizes.push(size);
            });
        } else {
            const mVal = normalizeInputQtyText(row.m);
            const lVal = normalizeInputQtyText(row.l);
            if (mVal !== '' || lVal !== '') {
                values.M = mVal;
                values.L = lVal;
                if (sizes.indexOf('M') === -1) sizes.push('M');
                if (sizes.indexOf('L') === -1) sizes.push('L');
            }
        }

        return {
            color: normalizeText(row.color),
            values: values,
            total: normalizeInputQtyText(row.total)
        };
    });

    if (sizes.length === 0) return buildDefaultInputQtyRows();

    const totals = {};
    const rawTotals = parseInputQtyTotals(inputData);
    rawTotals.forEach((item) => {
        const size = normalizeText(item ? item.size : '');
        if (size === '') return;
        totals[size] = normalizeInputQtyText(item ? item.qty : '');
    });

    if (!totals.M) totals.M = normalizeInputQtyText(inputData ? inputData._QTY_TOTAL_M : '');
    if (!totals.L) totals.L = normalizeInputQtyText(inputData ? inputData._QTY_TOTAL_L : '');

    sizes.forEach((size) => {
        if (normalizeText(totals[size]) !== '') return;
        totals[size] = sumQtyTexts(normalizedRows.map((row) => row.values[size]));
    });

    normalizedRows.forEach((row) => {
        if (normalizeText(row.total) !== '') return;
        row.total = sumQtyTexts(sizes.map((size) => row.values[size]));
    });

    // サイズ別セルがすべて空の行は、APIが返すtotalがあっても表示しない。
    normalizedRows.forEach((row) => {
        const hasAnySizeValue = sizes.some((size) => normalizeText(row.values[size]) !== '');
        if (!hasAnySizeValue) row.total = '';
    });

    return {
        sizes: sizes,
        rows: ensureMinInputQtyRows(normalizedRows, 6),
        totals: totals
    };
}

// カラーサイズ表の1セル変更をブロックデータへ反映し、行計/列計/総計を再計算する。
function updateInputQtyCell(blockData, rowIndex, size, value) {
    if (!blockData || normalizeText(size) === '') return blockData;
    const qtyMatrix = normalizeInputQtyMatrix(blockData);
    const rows = ensureMinInputQtyRows(qtyMatrix.rows, Math.max(6, rowIndex + 1)).map((row) => ({
        color: normalizeText(row.color),
        values: { ...((row && row.values) || {}) },
        total: normalizeText(row.total)
    }));

    // 変更セルの値を反映したあと、同じ行の合計を更新する。
    rows[rowIndex].values[size] = normalizeInputQtyText(value);
    rows[rowIndex].total = sumQtyTexts(qtyMatrix.sizes.map((item) => rows[rowIndex].values[item]));

    // サイズ別の列合計を再計算する。
    const totals = {};
    qtyMatrix.sizes.forEach((item) => {
        totals[item] = sumQtyTexts(rows.map((row) => row.values[item]));
    });
    const totalList = qtyMatrix.sizes.map((item) => ({ size: item, qty: totals[item] }));
    const grandTotal = sumQtyTexts(qtyMatrix.sizes.map((item) => totals[item]));

    return {
        ...blockData,
        _QTY_ROWS: rows,
        _QTY_TOTALS: totalList,
        _QTY_TOTAL_M: totals.M || '',
        _QTY_TOTAL_L: totals.L || '',
        TNYM_MDHatchusu: grandTotal
    };
}

// ブロック未返却時は空カードを作らない。
function buildEmptyInputBlocks() {
    return [];
}

// 投入数ブロック配列を最大件数制限つきで返す。
function normalizeInputBlocks(inputData) {
    // API優先: _INPUT_BLOCKS が返ってくればそれを採用
    const rawBlocks = parseInputBlocks(inputData);
    // ブロックがない場合はカードを表示しない
    if (rawBlocks.length === 0) return buildEmptyInputBlocks();

    // 最大表示件数(999)に制限し、非object要素は空object化して壊れを防止
    return rawBlocks.map((block) => {
        if (block && typeof block === 'object') return applyInputFieldDefaults(block);
        return applyInputFieldDefaults({});
    }).slice(0, MAX_BASIC_INFO_HINBAN_COUNT);
}

// 確定フラグは製商品発注済みの加工指示だけ入力可能にする。
export function isIssuedInputBlock(blockData) {
    const orderedQty = toNumber(valueOf(blockData, 'SSDM_SeiHachusu'));
    if (!Number.isNaN(orderedQty) && orderedQty > 0) return true;

    return valueOf(blockData, 'SSD_SeihinHachuDenno') !== '';
}

// 横幅に応じてカード幅を計算するため、対象要素の表示幅を監視する。
function isMdMainMenuOpen() {
    if (typeof document === 'undefined') return false;
    const panel = document.getElementById('menupanel');
    return !!panel && !!panel.querySelector('.MuiDrawer-paper');
}

function useObservedElementWidth(elementRef) {
    const [width, setWidth] = useState(0);
    const widthRef = useRef(0);
    const rafRef = useRef(0);

    useEffect(() => {
        if (!elementRef.current) return undefined;
        const target = elementRef.current;
        const applyWidth = (force = false) => {
            if (!force && isMdMainMenuOpen()) return;

            const nextWidth = target.clientWidth || 0;
            if (widthRef.current === nextWidth) return;

            widthRef.current = nextWidth;
            setWidth(nextWidth);
        };
        const scheduleApplyWidth = () => {
            if (rafRef.current) return;
            rafRef.current = window.requestAnimationFrame(() => {
                rafRef.current = 0;
                applyWidth();
            });
        };
        applyWidth(true);

        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => scheduleApplyWidth());
            observer.observe(target);
        }
        window.addEventListener('resize', scheduleApplyWidth);
        return () => {
            window.removeEventListener('resize', scheduleApplyWidth);
            if (observer) observer.disconnect();
            if (rafRef.current) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = 0;
            }
        };
    }, [elementRef]);

    return width;
}

// 投入数タブの1品番ブロック（基本項目/画像/数量表）を描画する。
// 投入数タブの1品番ブロック。左側入力、右側画像、下部カラーサイズ表をまとめて描画する。
const RenderInputBlock = React.memo(function ({
    blockIndex,
    blockData,
    onUnlink,
    onChangeField,
    onChangeQtyCell,
    onToggleFlag,
    onToggleStop,
    onMasterSearch,
    lockIssuedData = false,
    blockWidth = 860
}) {
    // 左/右カラムの定義は固定し、値だけ対象ブロックから取得する。
    const leftFields = useMemo(
        () => INPUT_LEFT_FIELD_DEFS.map((fieldDef) => ({ ...fieldDef, value: valueOf(blockData, fieldDef.key), displayValue: masterDisplayText(blockData, fieldDef) })),
        [blockData]
    );
    const rightFields = useMemo(
        () => INPUT_RIGHT_FIELD_DEFS.map((fieldDef) => ({ ...fieldDef, value: valueOf(blockData, fieldDef.key), displayValue: masterDisplayText(blockData, fieldDef) })),
        [blockData]
    );

    // カラー/サイズ表の表示行列を作成
    const qtyMatrix = useMemo(() => normalizeInputQtyMatrix(blockData), [blockData]);
    // データ有無にかかわらず、下段は常に6行ぶん表示する。
    const qtyDisplayRows = useMemo(() => ensureMinInputQtyRows(qtyMatrix.rows, 6), [qtyMatrix.rows]);
    // 画面幅に対して見せたい列数を基準に列幅を算出
    const qtyVisibleSizeCols = 5;
    const qtyUnifiedColWidth = Math.max(58, Math.floor(blockWidth / (qtyVisibleSizeCols + 2)));
    const qtyLeftFixedWidth = qtyUnifiedColWidth;
    const qtyTotalFixedWidth = qtyUnifiedColWidth;
    const qtySizeColWidth = qtyUnifiedColWidth;
    const qtyBodyRowHeight = 42;
    const qtyBodyInputHeight = 34;
    const qtyTableMaxHeight = 340;
    // 実データ列が少ない場合も表幅を揃えるため空列を補う
    const minSizeSlots = Math.max(qtyVisibleSizeCols, qtyMatrix.sizes.length);
    const sizeSlots = [];
    qtyMatrix.sizes.forEach((size, index) => {
        sizeSlots.push({ key: `size_${index}`, name: size, source: size });
    });
    for (let i = sizeSlots.length; i < minSizeSlots; i++) {
        sizeSlots.push({ key: `blank_${i}`, name: '', source: '' });
    }
    const qtyTableMinWidth = qtyLeftFixedWidth + (sizeSlots.length * qtySizeColWidth) + qtyTotalFixedWidth;
    // 右上領域（チェック＋画像）の固定高さ
    const rightTopHeight = 185;

    // APIが返した表示用URLをそのまま使う
    const imageUrl = valueOf(blockData, '_MAIN_IMAGE_URL');

    // 各チェックボックスの表示状態
    const stopChecked = valueOf(blockData, 'TNYM_StopFlg') === '1';
    const fixedChecked = valueOf(blockData, 'SSD_KanryoFlg') === '1';
    const fixedEnabled = isIssuedInputBlock(blockData);
    const temporaryBlock = valueOf(blockData, '_MD_TEMPORARY') === '1';
    // 発注済みカードは、仮カードを含めて確定フラグ以外を編集不可にする。
    const issuedLocked = Boolean(lockIssuedData) && fixedEnabled;
    const orderChecked = valueOf(blockData, '_ORDER_TARGET') === '1';
    const subMaterialChecked = valueOf(blockData, '_SUB_MATERIAL_TARGET') === '1';
    const brandDisplay = masterDisplayText({
        ...blockData,
        PDT_BrdCode: valueOf(blockData, 'PDT_BrdCode', valueOf(blockData, '_IMAGE_BRAND_CODE'))
    }, INPUT_BRAND_FIELD_DEF);
    const currencyFieldDef = INPUT_BOTTOM_FIELD_DEFS.find((fieldDef) => fieldDef.key === 'TNYM_SHD_Cur');
    const currencyDisplay = masterDisplayText(blockData, currencyFieldDef);

    const changeField = useCallback((key, value) => {
        if (issuedLocked) return;
        if (onChangeField) onChangeField(blockIndex, key, value);
    }, [blockIndex, issuedLocked, onChangeField]);

    const searchMaster = useCallback((fieldDef) => {
        if (issuedLocked) return;
        if (onMasterSearch) onMasterSearch(blockIndex, fieldDef, blockData);
    }, [blockIndex, blockData, issuedLocked, onMasterSearch]);

    const changeQtyCell = useCallback((rowIndex, size, value) => {
        if (issuedLocked) return;
        if (onChangeQtyCell) onChangeQtyCell(blockIndex, rowIndex, size, value);
    }, [blockIndex, issuedLocked, onChangeQtyCell]);

    return (
        <Paper variant="outlined" style={{ borderColor: COLORS.line, width: blockWidth, minWidth: blockWidth, flex: `0 0 ${blockWidth}px` }}>
            <Box display="flex" style={{ borderBottom: `1px solid ${COLORS.line}` }}>
                <Box width="50%" style={{ borderRight: `1px solid ${COLORS.line}` }}>
                    <Table size="small" style={{ tableLayout: 'fixed' }}>
                        <TableBody>
                            {leftFields.map((field, idx) => (
                                <FieldRow
                                    key={field.label}
                                    label={field.label}
                                    value={field.value}
                                    displayValue={field.displayValue}
                                    noInput={issuedLocked || field.readOnly || NO_INPUT_LEFT_INDEXES.has(idx) || NO_INPUT_FIELD_LABELS.has(field.label)}
                                    fieldDef={field}
                                    onChange={(value) => changeField(field.key, value)}
                                    onSearch={searchMaster}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                <Box width="50%">
                    <Box style={{ borderBottom: `1px solid ${COLORS.line}`, height: rightTopHeight, display: 'flex', flexDirection: 'column' }}>
                        <Box
                            style={{
                                minHeight: 30,
                                padding: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                flexWrap: 'nowrap'
                            }}
                        >
                            <Box display="flex" alignItems="center" style={{ whiteSpace: 'nowrap' }}>
                                <Checkbox
                                    size="small"
                                    checked={stopChecked}
                                    disabled={issuedLocked}
                                    // 中止フラグは親画面でAPI更新する
                                    onChange={(event) => {
                                        const checked = Boolean(event.target.checked);
                                        if (onToggleFlag) onToggleFlag(blockIndex, 'TNYM_StopFlg', checked);
                                        if (onToggleStop) onToggleStop(blockData, checked);
                                    }}
                                    style={{ padding: 4 }}
                                />
                                <Typography variant="caption">中止</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ whiteSpace: 'nowrap' }}>
                                <Checkbox
                                    size="small"
                                    checked={fixedChecked}
                                    disabled={!fixedEnabled}
                                    // 確定フラグは保存時にD_SSDへ反映するため、親へ画面状態を渡す。
                                    onChange={(event) => onToggleFlag && onToggleFlag(blockIndex, 'SSD_KanryoFlg', Boolean(event.target.checked))}
                                    style={{ padding: 4 }}
                                />
                                <Typography variant="caption">確定</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ whiteSpace: 'nowrap' }}>
                                <Checkbox
                                    size="small"
                                    checked={orderChecked}
                                    disabled={issuedLocked || temporaryBlock}
                                    // 製商品発注対象フラグはUI状態のみ更新
                                    onChange={(event) => onToggleFlag && onToggleFlag(blockIndex, '_ORDER_TARGET', Boolean(event.target.checked))}
                                    style={{ padding: 4 }}
                                />
                                <Typography variant="caption">製商品発注</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ whiteSpace: 'nowrap' }}>
                                <Checkbox
                                    size="small"
                                    checked={subMaterialChecked}
                                    disabled={issuedLocked || temporaryBlock}
                                    // 副資材登録対象フラグはUI状態のみ更新
                                    onChange={(event) => onToggleFlag && onToggleFlag(blockIndex, '_SUB_MATERIAL_TARGET', Boolean(event.target.checked))}
                                    style={{ padding: 4 }}
                                />
                                <Typography variant="caption">副資材登録</Typography>
                            </Box>
                        </Box>
                        <Box style={{ flex: 1, padding: 8 }}>
                            <InputBlockImage imageUrl={imageUrl} picturePath={imageUrl} />
                        </Box>
                    </Box>
                    <Box>
                        <Table size="small" style={{ tableLayout: 'fixed' }}>
                            <TableBody>
                                {rightFields.map((field, idx) => (
                                    <FieldRow
                                        key={field.label}
                                        label={field.label}
                                        value={field.value}
                                        displayValue={field.displayValue}
                                        labelWidth={82}
                                        noInput={issuedLocked || field.readOnly || NO_INPUT_RIGHT_INDEXES.has(idx) || NO_INPUT_FIELD_LABELS.has(field.label)}
                                        fieldDef={field}
                                        onChange={(value) => changeField(field.key, value)}
                                        onSearch={searchMaster}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            </Box>

            <Box style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: qtyTableMaxHeight, width: '100%', minWidth: 0, position: 'relative' }}>
                <Table size="small" style={{ tableLayout: 'fixed', minWidth: qtyTableMinWidth, width: 'max-content', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                style={{
                                    backgroundColor: COLORS.headBg,
                                    color: COLORS.headText,
                                    width: qtyLeftFixedWidth,
                                    minWidth: qtyLeftFixedWidth,
                                    maxWidth: qtyLeftFixedWidth,
                                    fontWeight: 700,
                                    padding: '4px 8px',
                                    borderColor: COLORS.line,
                                    borderRight: `1px solid ${COLORS.line}`,
                                    whiteSpace: 'nowrap',
                                    position: 'sticky',
                                    top: 0,
                                    left: 0,
                                    zIndex: 8,
                                    boxShadow: `inset -1px 0 0 ${COLORS.line}`,
                                    backgroundClip: 'padding-box',
                                    textAlign: 'center'
                                }}
                            >
                                カラー/サイズ
                            </TableCell>
                            {sizeSlots.map((slot) => (
                                <TableCell
                                    key={`input-size-head-${slot.key}`}
                                    style={{
                                        backgroundColor: COLORS.headBg,
                                        color: COLORS.headText,
                                        width: qtySizeColWidth,
                                        minWidth: qtySizeColWidth,
                                        fontWeight: 700,
                                        padding: '4px 8px',
                                        borderColor: COLORS.line,
                                        borderRight: `1px solid ${COLORS.line}`,
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 7,
                                        backgroundClip: 'padding-box'
                                    }}
                                >
                                    {slot.name}
                                </TableCell>
                            ))}
                            <TableCell
                                style={{
                                    backgroundColor: '#fff',
                                    borderColor: COLORS.line,
                                    borderLeft: 'none',
                                    borderRight: `1px solid ${COLORS.line}`,
                                    width: qtyTotalFixedWidth,
                                    minWidth: qtyTotalFixedWidth,
                                    position: 'sticky',
                                    top: 0,
                                    right: 0,
                                    zIndex: 9,
                                    boxShadow: `inset 1px 0 0 ${COLORS.line}, inset -1px 0 0 ${COLORS.line}`,
                                    backgroundClip: 'padding-box'
                                }}
                            />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {qtyDisplayRows.map((row, idx) => (
                            <TableRow key={`row-${idx}`} style={{ height: qtyBodyRowHeight }}>
                                {(() => {
                                    const rowBg = idx % 2 === 1 ? COLORS.listEvenBg : '#fff';
                                    return (
                                        <>
                                            <TableCell
                                                style={{
                                                    backgroundColor: COLORS.headBg,
                                                    color: COLORS.headText,
                                                    width: qtyLeftFixedWidth,
                                                    minWidth: qtyLeftFixedWidth,
                                                    maxWidth: qtyLeftFixedWidth,
                                                    borderColor: COLORS.line,
                                                    borderRight: `1px solid ${COLORS.line}`,
                                                    fontWeight: 700,
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 4,
                                                    boxShadow: `inset -1px 0 0 ${COLORS.line}`,
                                                    backgroundClip: 'padding-box',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                {row.color}
                                            </TableCell>
                                            {sizeSlots.map((slot) => (
                                                <TableCell
                                                    key={`input-size-cell-${idx}-${slot.key}`}
                                                    style={{ borderColor: COLORS.line, borderRight: `1px solid ${COLORS.line}`, backgroundColor: rowBg, padding: 4, width: qtySizeColWidth, minWidth: qtySizeColWidth }}
                                                >
                                                    {slot.source === '' || issuedLocked ? (
                                                        <ReadonlyInput
                                                            value={slot.source === '' ? '' : normalizeText(row.values[slot.source])}
                                                            align="center"
                                                            height={qtyBodyInputHeight}
                                                        />
                                                    ) : (
                                                        <EditableInput
                                                            value={normalizeText(row.values[slot.source])}
                                                            align="center"
                                                            height={qtyBodyInputHeight}
                                                            inputProps={{
                                                                maxLength: INPUT_QTY_MAX_LENGTH,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*'
                                                            }}
                                                            onChange={(value) => changeQtyCell(idx, slot.source, value)}
                                                        />
                                                    )}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                style={{
                                                    borderColor: COLORS.line,
                                                    borderLeft: 'none',
                                                    borderRight: `1px solid ${COLORS.line}`,
                                                    textAlign: 'center',
                                                    fontWeight: 700,
                                                    backgroundColor: rowBg,
                                                    width: qtyTotalFixedWidth,
                                                    minWidth: qtyTotalFixedWidth,
                                                    position: 'sticky',
                                                    right: 0,
                                                    zIndex: 5,
                                                    boxShadow: `inset 1px 0 0 ${COLORS.line}, inset -1px 0 0 ${COLORS.line}`,
                                                    backgroundClip: 'padding-box'
                                                }}
                                            >
                                                {row.total}
                                            </TableCell>
                                        </>
                                    );
                                })()}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell
                                style={{
                                    backgroundColor: COLORS.headBg,
                                    color: COLORS.headText,
                                    width: qtyLeftFixedWidth,
                                    minWidth: qtyLeftFixedWidth,
                                    maxWidth: qtyLeftFixedWidth,
                                    fontWeight: 700,
                                    padding: '4px 8px',
                                    borderColor: COLORS.line,
                                    borderRight: `1px solid ${COLORS.line}`,
                                    whiteSpace: 'nowrap',
                                    position: 'sticky',
                                    left: 0,
                                    bottom: 0,
                                    zIndex: 8,
                                    boxShadow: `inset -1px 0 0 ${COLORS.line}`,
                                    backgroundClip: 'padding-box',
                                    textAlign: 'center'
                                }}
                            >
                                Total
                            </TableCell>
                            {sizeSlots.map((slot) => (
                                <TableCell
                                    key={`input-size-total-${slot.key}`}
                                    style={{
                                        backgroundColor: COLORS.headBg,
                                        color: COLORS.headText,
                                        borderColor: COLORS.line,
                                        borderRight: `1px solid ${COLORS.line}`,
                                        width: qtySizeColWidth,
                                        minWidth: qtySizeColWidth,
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        position: 'sticky',
                                        bottom: 0,
                                        zIndex: 6,
                                        backgroundClip: 'padding-box'
                                    }}
                                >
                                    {normalizeText(slot.source === '' ? '' : qtyMatrix.totals[slot.source])}
                                </TableCell>
                            ))}
                            <TableCell
                                style={{
                                    backgroundColor: COLORS.headBg,
                                    borderColor: COLORS.line,
                                    borderLeft: 'none',
                                    borderRight: `1px solid ${COLORS.line}`,
                                    width: qtyTotalFixedWidth,
                                    minWidth: qtyTotalFixedWidth,
                                    position: 'sticky',
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 8,
                                    boxShadow: `inset 1px 0 0 ${COLORS.line}, inset -1px 0 0 ${COLORS.line}`,
                                    backgroundClip: 'padding-box'
                                }}
                            />
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>

            <Table size="small" style={{ tableLayout: 'fixed' }}>
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={2} style={{ borderColor: COLORS.line }}>
                            加工指示No: {valueOf(blockData, 'SSD_No')}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell style={{ width: '50%', borderColor: COLORS.line, borderRight: `1px solid ${COLORS.line}` }}>
                            ブランド: {brandDisplay}
                        </TableCell>
                        <TableCell style={{ width: '50%', borderColor: COLORS.line }}>
                            実MD単価:    {valueOf(blockData, 'TNYM_MDHatchuTanka')}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell style={{ borderColor: COLORS.line, borderRight: `1px solid ${COLORS.line}` }}>
                            ロット換算: {valueOf(blockData, 'SSD_RotRound')}
                        </TableCell>
                        <TableCell style={{ borderColor: COLORS.line }}>
                            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    通貨単位:    {currencyDisplay}
                                </Box>
                                {currencyFieldDef && currencyFieldDef.searchable ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => searchMaster(currencyFieldDef)}
                                        style={{ padding: 2, width: 24, height: 24 }}
                                        title="通貨単位を検索"
                                    >
                                        <Icon style={{ fontSize: 18 }}>arrow_right</Icon>
                                    </IconButton>
                                ) : null}
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell style={{ borderColor: COLORS.line, borderRight: `1px solid ${COLORS.line}` }}>
                            発注状態: {valueOf(blockData, 'SHD_SynFlg1')}
                        </TableCell>
                        <TableCell style={{ borderColor: COLORS.line }}>
                            レート:      {valueOf(blockData, 'TNYM_Rate')}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell style={{ borderColor: COLORS.line, borderRight: `1px solid ${COLORS.line}` }} />
                        <TableCell style={{ borderColor: COLORS.line, textAlign: 'right', padding: 8 }}>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={issuedLocked}
                                style={{ backgroundColor: COLORS.btn, color: COLORS.btnText }}
                                onClick={() => !issuedLocked && onUnlink && onUnlink(blockData)}
                            >
                                紐づけ削除
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Paper>
    );
});

// 投入数タブ全体（複数ブロック横並び）を描画する。
// 投入数タブ本体。複数品番ブロックを横並びにし、登録済品番追加などの操作を受ける。
function RenderInputTab({
    inputData,
    fnc,
    jutakuHinFlg = '',
    onAddExists,
    onAddNew,
    onOrder,
    onSubMaterial,
    onUnlink,
    onToggleStop,
    onChangeBlocks,
    lockIssuedData = false,
    commandsDisabled = false
}) {
    const inputScrollRef = useRef(null);
    const inputViewportWidth = useObservedElementWidth(inputScrollRef);
    const [blocksState, setBlocksState] = useState(null);
    const [scrollLeft, setScrollLeft] = useState(0);
    const blocksStateRef = useRef([]);
    const actionBlocksRef = useRef([]);
    const scrollRafRef = useRef(0);
    const onChangeBlocksRef = useRef(onChangeBlocks);
    const onUnlinkRef = useRef(onUnlink);
    const onToggleStopRef = useRef(onToggleStop);

    const cardGap = 8;
    const inputVisibleCards = 2.3;
    const inputBaseCardWidth = 800;
    const cardWidth = useMemo(() => {
        if (inputViewportWidth <= 0) return inputBaseCardWidth;
        const gapTotal = cardGap * Math.max(inputVisibleCards - 1, 0);
        const raw = Math.floor((inputViewportWidth - gapTotal) / inputVisibleCards);
        return Math.max(680, raw);
    }, [inputViewportWidth, cardGap]);
    const blocks = useMemo(() => normalizeInputBlocks(inputData), [inputData]);

    useEffect(() => {
        onChangeBlocksRef.current = onChangeBlocks;
        onUnlinkRef.current = onUnlink;
        onToggleStopRef.current = onToggleStop;
    }, [onChangeBlocks, onUnlink, onToggleStop]);

    useEffect(() => {
        const currentBlocks = blocksStateRef.current;
        const isSameRows = currentBlocks.length === blocks.length && currentBlocks.every((row, idx) => row === blocks[idx]);
        if (isSameRows) return;

        const nextBlocks = blocks.map((row) => ({ ...row }));
        blocksStateRef.current = nextBlocks;
        setBlocksState(nextBlocks);
        if (onChangeBlocksRef.current) onChangeBlocksRef.current(nextBlocks);
    }, [blocks]);

    const scheduleParentSync = useCallback((nextBlocks) => {
        blocksStateRef.current = nextBlocks;
        if (onChangeBlocksRef.current) onChangeBlocksRef.current(nextBlocks);
    }, []);

    const actionBlocks = blocksState === null ? blocks : blocksState;
    actionBlocksRef.current = actionBlocks;

    useEffect(() => {
        setScrollLeft(0);
        if (inputScrollRef.current) inputScrollRef.current.scrollLeft = 0;
    }, [actionBlocks.length]);

    const updateBlock = useCallback((index, updater) => {
        const nextBlocks = actionBlocksRef.current.map((row, i) => {
            if (i !== index) return row;
            return updater(row);
        });
        blocksStateRef.current = nextBlocks;
        setBlocksState(nextBlocks);
        scheduleParentSync(nextBlocks);
        if (fnc && fnc.current && fnc.current.modify) fnc.current.modify();
    }, [fnc, scheduleParentSync]);

    const updateBlockField = useCallback((index, key, value) => {
        updateBlock(index, (row) => ({ ...row, [key]: value }));
    }, [updateBlock]);

    const onInputScroll = useCallback((event) => {
        const nextLeft = event.currentTarget ? event.currentTarget.scrollLeft || 0 : 0;
        if (scrollRafRef.current) window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = window.requestAnimationFrame(() => {
            scrollRafRef.current = 0;
            setScrollLeft(nextLeft);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (scrollRafRef.current) {
                window.cancelAnimationFrame(scrollRafRef.current);
                scrollRafRef.current = 0;
            }
        };
    }, []);

    const openMasterSearch = useCallback((index, fieldDef, blockData) => {
        if (!fieldDef || fieldDef.dataType !== MD_FIELD_TYPE.MASTER) return;
        if (!fieldDef.searchable) return;
        if (!fnc || !fnc.current || !fnc.current.searchlistopen) return;

        // 仕入先は品番ダイアログと同じ共通検索条件を使用する。
        // V_SUPPLIER側で仕入先区分・代表サブコードを限定し、
        // TSIComLibraryのMDTOUNYU条件で受託品区分を絞り込む。
        if (fieldDef.key === 'TNYM_SiireSakiCd') {
            const currentSupplier = normalizeText(blockData ? blockData[fieldDef.key] : '');
            const currentJutakuHinFlg = normalizeText(
                jutakuHinFlg,
                normalizeText(blockData ? blockData.TNY_JUTAKUHINFLG : '')
            );
            fnc.current.searchlistopen({
                label: fieldDef.label,
                multiple: false,
                rowmode: false,
                data: {
                    dataid: 'V_SUPPLIER:MDTOUNYU',
                    columnid: fieldDef.key,
                    columnnm: fieldDef.label,
                    datatype: String(MD_FIELD_TYPE.MASTERCODE),
                    initial: true,
                    initialSelectedCodes: currentSupplier === '' ? [] : [currentSupplier]
                },
                rowdata: {
                    ...((blockData && typeof blockData === 'object') ? blockData : {}),
                    TNY_JUTAKUHINFLG: currentJutakuHinFlg
                },
                doApply: function (eventData) {
                    const row = pickSearchRow(eventData);
                    const code = normalizeText(
                        eventData ? eventData.value : '',
                        normalizeText(row ? row.TOR_CODE : '')
                    );
                    const name = normalizeText(
                        row ? row.TOR_NAMERNM : '',
                        normalizeText(row ? row.TOR_NAME : '', normalizeText(row ? row.name : ''))
                    );
                    if (code === '') {
                        if (fnc.current && fnc.current.searchlistclose) fnc.current.searchlistclose();
                        return;
                    }

                    updateBlock(index, (currentRow) => ({
                        ...currentRow,
                        [fieldDef.key]: code,
                        ...(fieldDef.nameKey ? { [fieldDef.nameKey]: name } : {}),
                        TNYM_SiireSakiKbn: normalizeText(row ? row.TOR_KUBUN : '', '1'),
                        TNYM_SiireSakiSubCd: normalizeText(row ? row.TOR_SUBCODE : '', '00000')
                    }));

                    if (fnc.current && fnc.current.modify) fnc.current.modify();
                    if (fnc.current && fnc.current.searchlistclose) fnc.current.searchlistclose();
                }
            });
            return;
        }

        // 加工先も品番ダイアログと同じ共通検索を使用し、
        // ブランド・仕入先・製商品区分に対応するV_FACTORYだけを候補にする。
        if (fieldDef.key === 'TNYM_KakouSakiSubCd') {
            const currentProcessorSubCode = normalizeText(blockData ? blockData[fieldDef.key] : '');
            const brandCode = normalizeText(
                blockData ? blockData.PDT_BrdCode : '',
                normalizeText(blockData ? blockData._IMAGE_BRAND_CODE : '')
            );
            const supplierCode = normalizeText(blockData ? blockData.TNYM_SiireSakiCd : '');
            if (brandCode === '') {
                if (fnc.current.warnmsg) fnc.current.warnmsg('先にブランドを選択してください。');
                return;
            }
            if (supplierCode === '') {
                if (fnc.current.warnmsg) fnc.current.warnmsg('先に仕入先を選択してください。');
                return;
            }

            fnc.current.searchlistopen({
                label: fieldDef.label,
                multiple: false,
                rowmode: false,
                data: {
                    dataid: 'V_FACTORY:MDTOUNYU',
                    columnid: fieldDef.key,
                    columnnm: fieldDef.label,
                    datatype: String(MD_FIELD_TYPE.MASTERCODE),
                    initial: true,
                    // V_FACTORYの検索行IDは加工先サブコード。
                    initialSelectedCodes: currentProcessorSubCode === '' ? [] : [currentProcessorSubCode]
                },
                rowdata: {
                    ...((blockData && typeof blockData === 'object') ? blockData : {}),
                    PDT_BRDCODE: brandCode,
                    TNYM_SIIRESAKICD: supplierCode,
                    PDT_SHOHINKBN: normalizeText(blockData ? blockData.PDT_SHOHINKBN : '', '01')
                },
                doApply: function (eventData) {
                    const row = pickSearchRow(eventData);
                    // V_FACTORYの選択値はTOR_SUBCODE。TOR_CODEはD_TNYMの内部値として別に保持する。
                    const parentCode = normalizeText(
                        row ? row.TOR_CODE : '',
                        supplierCode
                    );
                    const subCode = normalizeText(
                        row ? row.TOR_SUBCODE : '',
                        normalizeText(eventData ? eventData.value : '')
                    );
                    const name = normalizeText(
                        row ? row.TOR_SUBCDNAMERNM : '',
                        normalizeText(
                            row ? row.TOR_NAMERNM : '',
                            normalizeText(row ? row.TOR_SUBCDNAME : '', normalizeText(row ? row.TOR_NAME : '', normalizeText(row ? row.name : '')))
                        )
                    );
                    if (parentCode === '' || subCode === '') {
                        if (fnc.current && fnc.current.searchlistclose) fnc.current.searchlistclose();
                        return;
                    }

                    updateBlock(index, (currentRow) => ({
                        ...currentRow,
                        TNYM_KakouSakiCd: parentCode,
                        [fieldDef.key]: subCode,
                        ...(fieldDef.nameKey ? { [fieldDef.nameKey]: name } : {}),
                        TNYM_KakouSakiKbn: normalizeText(row ? row.TOR_KUBUN : '', '2')
                    }));

                    if (fnc.current && fnc.current.modify) fnc.current.modify();
                    if (fnc.current && fnc.current.searchlistclose) fnc.current.searchlistclose();
                }
            });
            return;
        }

        if (!fnc.current.postaxios) return;

        fnc.current.postaxios("mdtounyu/GetInputMasterOptions", {
            type: fieldDef.key,
            rowdata: [{ ...((blockData && typeof blockData === 'object') ? blockData : {}) }]
        }, {
            onSuccess: function (data) {
                const options = Array.isArray(data && data.rowdata)
                    ? data.rowdata
                        .map((row) => ({
                            rowid: normalizeText(row.rowid, normalizeText(row.code)),
                            code: normalizeText(row.code),
                            name: normalizeText(row.name)
                        }))
                        .filter((row) => row.code !== '')
                    : [];

                if (options.length === 0) {
                    if (fnc.current && fnc.current.wait) fnc.current.wait(false);
                    if (fnc.current && fnc.current.warnmsg) fnc.current.warnmsg(`${fieldDef.label}の検索候補がありません。`);
                    return;
                }

                // 候補取得APIの待機状態を解除してから、静的候補の検索リストを表示する。
                if (fnc.current && fnc.current.wait) fnc.current.wait(false);
                fnc.current.searchlistopen({
                    label: fieldDef.label,
                    multiple: false,
                    rowmode: false,
                    data: {
                        dataid: '',
                        columnid: fieldDef.key,
                        columnnm: fieldDef.label,
                        // 共通SearchListはdatatypeを文字列のCELLTYPE値で判定するため、
                        // MD画面内の数値定義ではなく文字列に揃えてSELECT候補として開く。
                        datatype: String(MD_FIELD_TYPE.SELECT),
                        options: options
                    },
                    rowdata: blockData,
                    doApply: function (eventData) {
                        const row = pickSearchRow(eventData);
                        const code = normalizeText(eventData ? eventData.value : '', normalizeText(row ? row.code : ''));
                        const name = normalizeText(row ? row.name : '');
                        if (code === '') {
                            if (fnc.current && fnc.current.searchlistclose) fnc.current.searchlistclose();
                            return;
                        }

                        updateBlock(index, (currentRow) => ({
                            ...currentRow,
                            [fieldDef.key]: code,
                            ...(fieldDef.nameKey ? { [fieldDef.nameKey]: name } : {})
                        }));

                        if (fnc.current && fnc.current.modify) fnc.current.modify();
                        if (fnc.current && fnc.current.searchlistclose) fnc.current.searchlistclose();
                    }
                });
            },
            onError: function (data) {
                if (fnc.current && fnc.current.wait) fnc.current.wait(false);
                if (fnc.current && fnc.current.warnmsg) {
                    const message = data && data.errDescription ? data.errDescription : `${fieldDef.label}の検索候補取得に失敗しました。`;
                    fnc.current.warnmsg(message);
                }
            }
        });
    }, [fnc, jutakuHinFlg, updateBlock]);

    const updateBlockQtyCell = useCallback((index, rowIndex, size, value) => {
        updateBlock(index, (row) => updateInputQtyCell(row, rowIndex, size, value));
    }, [updateBlock]);

    const updateBlockFlag = useCallback((index, key, checked) => {
        // 対象ブロックだけを差分更新し、保存用の親stateへも同期する。
        updateBlock(index, (row) => ({ ...row, [key]: checked ? '1' : '0' }));
    }, [updateBlock]);

    const unlinkBlock = useCallback((blockData) => {
        if (onUnlinkRef.current) onUnlinkRef.current(blockData);
    }, []);

    const toggleStopBlock = useCallback((blockData, checked) => {
        if (onToggleStopRef.current) onToggleStopRef.current(blockData, checked);
    }, []);

    const rowWidth = Math.max(
        inputViewportWidth || inputBaseCardWidth,
        (actionBlocks.length * cardWidth) + (Math.max(actionBlocks.length - 1, 0) * cardGap)
    );
    const effectiveInputViewportWidth = inputViewportWidth || inputBaseCardWidth;
    const showHorizontalScroll = effectiveInputViewportWidth > 0 && (rowWidth > (effectiveInputViewportWidth + 1));
    const cardSpan = cardWidth + cardGap;
    const renderStart = showHorizontalScroll
        ? Math.max(0, Math.floor(scrollLeft / cardSpan) - 1)
        : 0;
    const renderCount = showHorizontalScroll
        ? Math.ceil(effectiveInputViewportWidth / cardSpan) + 3
        : actionBlocks.length;
    const renderEnd = Math.min(actionBlocks.length, renderStart + renderCount);
    const visibleBlocks = actionBlocks.slice(renderStart, renderEnd);
    const leftSpacerWidth = renderStart > 0 ? Math.max(0, (renderStart * cardSpan) - cardGap) : 0;
    const rightSpacerWidth = renderEnd < actionBlocks.length ? Math.max(0, ((actionBlocks.length - renderEnd) * cardSpan) - cardGap) : 0;

    return (
        <Box>
            <Box display="flex" gridGap={8} marginBottom={1} flexWrap="wrap">
                <Button size="small" variant="contained" style={{ backgroundColor: COLORS.btn, color: COLORS.btnText }} onClick={() => onAddExists && onAddExists(actionBlocks)}>登録済品番追加</Button>
                <Button size="small" variant="contained" style={{ backgroundColor: COLORS.btn, color: COLORS.btnText }} onClick={() => onAddNew && onAddNew(actionBlocks)}>新規品番追加</Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={commandsDisabled}
                    style={{ backgroundColor: commandsDisabled ? '#bdbdbd' : COLORS.btn, color: COLORS.btnText }}
                    onClick={() => !commandsDisabled && onOrder && onOrder(actionBlocks)}
                >
                    製商品発注
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={commandsDisabled}
                    style={{ backgroundColor: commandsDisabled ? '#bdbdbd' : COLORS.btn, color: COLORS.btnText }}
                    onClick={() => !commandsDisabled && onSubMaterial && onSubMaterial(actionBlocks)}
                >
                    副資材登録
                </Button>
            </Box>

            <Box ref={inputScrollRef} onScroll={onInputScroll} style={{ overflowX: showHorizontalScroll ? 'auto' : 'hidden', overflowY: 'hidden', width: '100%', minWidth: 0 }}>
                <Box display="flex" gridGap={cardGap} style={{ minWidth: rowWidth, width: 'max-content', flexWrap: 'nowrap' }}>
                    {leftSpacerWidth > 0 ? <Box style={{ width: leftSpacerWidth, minWidth: leftSpacerWidth }} /> : null}
                    {visibleBlocks.map((blockData, visibleIdx) => {
                        const idx = renderStart + visibleIdx;
                        return (
                        <RenderInputBlock
                            key={`input-block-${idx}`}
                            blockIndex={idx}
                            blockData={blockData}
                            onUnlink={unlinkBlock}
                            onChangeField={updateBlockField}
                            onChangeQtyCell={updateBlockQtyCell}
                            onToggleStop={toggleStopBlock}
                            onToggleFlag={updateBlockFlag}
                            onMasterSearch={openMasterSearch}
                            lockIssuedData={lockIssuedData}
                            blockWidth={cardWidth}
                        />
                        );
                    })}
                    {rightSpacerWidth > 0 ? <Box style={{ width: rightSpacerWidth, minWidth: rightSpacerWidth }} /> : null}
                </Box>
            </Box>
        </Box>
    );
}

// 受注数ブロックをAG Grid表示行へ展開する。
function toOrderGridRows(card) {
    const rows = [];
    let rowNo = 0;
    card.groups.forEach((group) => {
        group.rows.forEach((row, rowIndex) => {
            rows.push({
                rowid: `ord_${rowNo++}`,
                rowType: 'detail',
                grip: '',
                color: rowIndex === 0 ? group.color : '',
                size: row.size,
                qty: row.qty,
                store: row.store,
                channel: row.channel
            });
        });
        rows.push({
            rowid: `ord_${rowNo++}`,
            rowType: 'colorTotal',
            label: 'カラー計',
            qty: normalizeText(group.total, calcQty(group.rows))
        });
    });
    rows.push({
        rowid: `ord_${rowNo++}`,
        rowType: 'qtyTotal',
        label: '数量合計',
        qty: card.total
    });
    return rows;
}

// 受注数GRIDのセルスタイル。集計行だけヘッダ色にして通常明細と区別する。
function orderCellStyle(align = 'left', bold = false) {
    return (params) => {
        const style = {
            textAlign: align
        };
        if (bold) style.fontWeight = 700;
        if (params.data && params.data.rowType === 'colorTotal') {
            style.backgroundColor = '#c7e3e3';
            if (params.column && params.column.colId === 'grip') {
                style.textAlign = 'left';
                style.paddingLeft = 6;
            }
        }
        if (params.data && params.data.rowType === 'qtyTotal') {
            style.backgroundColor = '#c7e3e3';
            if (params.column && params.column.colId === 'grip') {
                style.textAlign = 'left';
                style.paddingLeft = 6;
            }
        }
        return style;
    };
}

// 受注数タブの列定義を幅スケール付きで組み立てる。
function buildOrderColumnDefs(widthScale = 1) {
    const scaleWidth = (base, min = 24) => Math.max(min, Math.round(base * widthScale));
    return [
        {
            field: 'grip',
            headerName: '',
            width: scaleWidth(48, 32),
            minWidth: scaleWidth(48, 32),
            maxWidth: scaleWidth(48, 32),
            lockPosition: true,
            rowDrag: (params) => params && params.data && params.data.rowType === 'detail',
            valueGetter: (params) => {
                if (!params.data || params.data.rowType === 'detail') return '';
                return params.data.label || '';
            },
            colSpan: (params) => {
                if (!params.data || params.data.rowType === 'detail') return 1;
                return 2;
            },
            sortable: false,
            resizable: false,
            suppressMovable: true,
            suppressMenu: true,
            cellStyle: (params) => {
                if (params.data && params.data.rowType !== 'detail') return orderCellStyle('left', false)(params);
                return {
                    textAlign: 'center',
                    color: '#9aa0a6'
                };
            }
        },
        {
            field: 'color',
            headerName: 'カラー',
            width: scaleWidth(96, 64),
            minWidth: scaleWidth(96, 64),
            flex: 1.1,
            valueGetter: (params) => {
                if (!params.data) return '';
                if (params.data.rowType !== 'detail') return '';
                return params.data.color;
            },
            cellStyle: orderCellStyle('center', false)
        },
        {
            field: 'size',
            headerName: 'サイズ',
            width: scaleWidth(72, 52),
            minWidth: scaleWidth(72, 52),
            flex: 0.8,
            valueGetter: (params) => {
                if (!params.data || params.data.rowType !== 'detail') return '';
                return params.data.size;
            },
            cellStyle: orderCellStyle('center', false)
        },
        {
            field: 'qty',
            headerName: '数量',
            width: scaleWidth(80, 56),
            minWidth: scaleWidth(80, 56),
            flex: 0.9,
            cellStyle: orderCellStyle('right', false)
        },
        {
            field: 'store',
            headerName: '店舗',
            width: scaleWidth(80, 56),
            minWidth: scaleWidth(80, 56),
            flex: 0.9,
            valueGetter: (params) => {
                if (!params.data || params.data.rowType !== 'detail') return '';
                return params.data.store;
            },
            cellStyle: orderCellStyle('center', false)
        },
        {
            field: 'channel',
            headerName: 'チャネル',
            width: scaleWidth(96, 64),
            minWidth: scaleWidth(96, 64),
            flex: 1.0,
            valueGetter: (params) => {
                if (!params.data || params.data.rowType !== 'detail') return '';
                return params.data.channel;
            },
            cellStyle: orderCellStyle('center', false)
        }
    ];
}

// 納品予定ブロックをAG Grid表示行へ展開する。
function toDeliveryGridRows(block) {
    const rows = block.rows.map((row, idx) => ({
        rowid: `del_${idx}`,
        rowType: 'detail',
        grip: '⋮⋮',
        color: row.color,
        size: row.size,
        inputQty: row.inputQty,
        actualQty: row.actualQty,
        planQty: row.planQty,
        centerQty: row.centerQty,
        remainQty: row.remainQty
    }));
    rows.push({
        rowid: 'del_summary_label',
        rowType: 'summaryLabel',
        label: '',
        inputQty: '投入数計',
        actualQty: '仕入実績計',
        planQty: '納品予定計',
        centerQty: 'センター納品計',
        remainQty: '残数計'
    });
    rows.push({
        rowid: 'del_summary_total',
        rowType: 'summaryTotal',
        label: '計',
        color: '',
        inputQty: block.totals.inputQty,
        actualQty: block.totals.actualQty,
        planQty: block.totals.planQty,
        centerQty: block.totals.centerQty,
        remainQty: block.totals.remainQty
    });
    return rows;
}

// 納品予定GRIDのセルスタイル。集計見出し/集計値の行だけ強調する。
function deliveryCellStyle(align = 'center', bold = false) {
    return (params) => {
        const style = {
            textAlign: align,
            fontWeight: bold ? 700 : 400
        };
        if (params.data && params.data.rowType === 'summaryLabel') {
            style.borderRight = 'none';
        }
        if (params.data && params.data.rowType === 'summaryLabel') {
            style.backgroundColor = COLORS.headBg;
            style.color = COLORS.headText;
            style.fontWeight = 700;
            style.fontSize = 11;
            style.textAlign = 'center';
        }
        if (params.data && params.data.rowType === 'summaryTotal') {
            style.fontWeight = 700;
            style.borderRight = 'none';
            style.textAlign = 'center';
        }
        return style;
    };
}

// 納品予定GRIDの列幅をカード幅に合わせてスケーリングする。
function buildDeliveryColumnWidths(widthScale = 1) {
    const scaleWidth = (base, min = 24) => Math.max(min, Math.round(base * widthScale));
    return {
        grip: scaleWidth(48, 32),
        color: scaleWidth(96, 64),
        size: scaleWidth(54, 42),
        inputQty: scaleWidth(62, 46),
        actualQty: scaleWidth(62, 46),
        planQty: scaleWidth(70, 50),
        centerQty: scaleWidth(80, 56),
        remainQty: scaleWidth(50, 38)
    };
}

// 納品予定タブの列定義を幅スケール付きで組み立てる。
function buildDeliveryColumnDefs(widthScale = 1) {
    const widths = buildDeliveryColumnWidths(widthScale);
    return [
        {
            field: 'grip',
            headerName: '',
            width: widths.grip,
            minWidth: widths.grip,
            maxWidth: widths.grip,
            lockPosition: true,
            rowDrag: (params) => params && params.data && params.data.rowType === 'detail',
            valueGetter: (params) => {
                if (!params.data || params.data.rowType === 'detail') return '';
                return params.data.label || '';
            },
            colSpan: (params) => {
                if (!params.data) return 1;
                if (params.data.rowType === 'summaryLabel' || params.data.rowType === 'summaryTotal') return 2;
                return 1;
            },
            sortable: false,
            resizable: false,
            suppressMovable: true,
            suppressMenu: true,
            cellStyle: (params) => {
                if (params.data && (params.data.rowType === 'summaryLabel' || params.data.rowType === 'summaryTotal')) {
                    const style = deliveryCellStyle('left', false)(params);
                    style.textAlign = 'left';
                    style.paddingLeft = 6;
                    return style;
                }
                if (params.data && params.data.rowType !== 'detail') return deliveryCellStyle('center', false)(params);
                return {
                    textAlign: 'center',
                    color: '#9aa0a6',
                    fontSize: 10
                };
            }
        },
        {
            field: 'color',
            headerName: 'カラー',
            width: widths.color,
            minWidth: widths.color,
            valueGetter: (params) => {
                if (!params.data || params.data.rowType !== 'detail') return '';
                return params.data.color;
            },
            cellStyle: deliveryCellStyle('center', false)
        },
        {
            field: 'size',
            headerName: 'サイズ',
            width: widths.size,
            minWidth: widths.size,
            valueGetter: (params) => {
                if (!params.data || params.data.rowType !== 'detail') return '';
                return params.data.size;
            },
            cellStyle: deliveryCellStyle('center', false)
        },
        {
            field: 'inputQty',
            headerName: '投入数',
            width: widths.inputQty,
            minWidth: widths.inputQty,
            cellStyle: deliveryCellStyle('center', false)
        },
        {
            field: 'actualQty',
            headerName: '仕入実績数',
            width: widths.actualQty,
            minWidth: widths.actualQty,
            cellStyle: deliveryCellStyle('center', false)
        },
        {
            field: 'planQty',
            headerName: '納品予定数',
            width: widths.planQty,
            minWidth: widths.planQty,
            cellStyle: deliveryCellStyle('center', false)
        },
        {
            field: 'centerQty',
            headerName: 'センター納品数',
            width: widths.centerQty,
            minWidth: widths.centerQty,
            cellStyle: deliveryCellStyle('center', false)
        },
        {
            field: 'remainQty',
            headerName: '残数',
            width: widths.remainQty,
            minWidth: widths.remainQty,
            cellStyle: deliveryCellStyle('center', false)
        }
    ];
}

// 日付ヘッダーは空でもセル高さを保つため、空白文字を返す。
function deliveryDateText(value) {
    const text = normalizeText(value);
    return text === '' ? ' ' : text;
}

// 納品予定上段の日付セルを、下段GRID列幅と一致させる。
function deliveryDateHeaderCellStyle(width) {
    return {
        width: width,
        minWidth: width,
        height: 26,
        border: `1px solid ${COLORS.line}`,
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
    };
}

// 日付文字列とカレンダーアイコンを横並びで描画する。
function deliveryDateHeaderCell(value) {
    return (
        <Box display="flex" alignItems="center" justifyContent="center" style={{ width: '100%', gap: 4, padding: '0 4px', boxSizing: 'border-box' }}>
            <Typography variant="caption" style={{ fontWeight: 700, lineHeight: 1 }}>{deliveryDateText(value)}</Typography>
            <Icon style={{ fontSize: 12, color: '#666' }}>calendar_today</Icon>
        </Box>
    );
}

// 受注数タブ本体。必要時のみ横スクロール表示する。
function RenderOrderTab({ orderData, onApplyInput, themeStyle, popupParent }) {
    const cardGap = 8;
    const orderVisibleCards = 3;
    const orderBaseCardWidth = 520;
    const orderScrollRef = useRef(null);
    const orderViewportWidth = useObservedElementWidth(orderScrollRef);
    const orderHeaderStyle = useMemo(() => ({
        backgroundColor: themeStyle && themeStyle.header ? themeStyle.header.back : '#93A5A1',
        color: themeStyle && themeStyle.header ? themeStyle.header.color : '#FFFFFF'
    }), [themeStyle]);
    const defaultColDef = useMemo(() => ({
        filter: false,
        sortable: false,
        resizable: false,
        editable: false,
        suppressMovable: true,
        menuTabs: ['generalMenuTab'],
        headerStyle: orderHeaderStyle
    }), [orderHeaderStyle]);

    const summary = useMemo(() => ({
        tenjino: valueOf(orderData, 'SJC_TENJINO'),
        tenjimei: valueOf(orderData, 'TNJ_NAME'),
        hinban: valueOf(orderData, '_TOP_HINBAN', valueOf(orderData, 'SJCM_HINBAN'))
    }), [orderData]);

    const cards = useMemo(() => normalizeOrderBlocks(orderData), [orderData]);
    const orderGridRows = useMemo(() => cards.map((card) => toOrderGridRows(card)), [cards]);
    // 3ブロック基準で、カードの外側padding込みで外枠内に収める。
    const cardWidth = useMemo(() => {
        if (orderViewportWidth <= 0) return orderBaseCardWidth;
        const gapTotal = cardGap * Math.max(orderVisibleCards - 1, 0);
        const raw = Math.floor((orderViewportWidth - gapTotal) / orderVisibleCards);
        return Math.max(320, raw);
    }, [orderViewportWidth, cardGap]);
    const orderGridScale = useMemo(() => cardWidth / orderBaseCardWidth, [cardWidth]);
    const columnDefs = useMemo(() => buildOrderColumnDefs(orderGridScale), [orderGridScale]);
    const rowWidth = Math.max(
        orderViewportWidth || cardWidth,
        (cards.length * cardWidth) + (Math.max(cards.length - 1, 0) * cardGap)
    );
    const showHorizontalScroll = orderViewportWidth > 0 && (rowWidth > (orderViewportWidth + 1));

    return (
        <Paper square variant="outlined" style={{ borderColor: COLORS.line, backgroundColor: 'transparent', borderRadius: 0, padding: 6, minWidth: 0 }}>
            <style>
                {`
                .md-order-grid.ag-theme-balham .ag-layout-auto-height .ag-center-cols-viewport,
                .md-order-grid.ag-theme-balham .ag-layout-auto-height .ag-center-cols-container,
                .md-order-grid.ag-theme-balham .ag-layout-auto-height .ag-center-cols-clipper {
                    min-height: unset !important;
                }
                `}
            </style>
            <Box style={{ padding: 6 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={0}>
                    <Box style={{ paddingLeft: 4 }}>
                        <Box display="flex" gridGap={24}>
                            <Box>
                                <Typography variant="caption" style={{ color: '#666' }}>展示会No</Typography>
                                <Typography variant="body2">{summary.tenjino}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" style={{ color: '#666' }}>展示会名称</Typography>
                                <Typography variant="body2">{summary.tenjimei}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Button
                        size="small"
                        variant="contained"
                        style={{ backgroundColor: COLORS.btn, color: '#fff' }}
                        // 受注数集計を投入数へ反映（親画面で表示のみ更新）
                        onClick={() => onApplyInput && onApplyInput(cards)}
                    >
                        投入数に反映
                    </Button>
                </Box>

                <Box ref={orderScrollRef} style={{ overflowX: showHorizontalScroll ? 'auto' : 'hidden', overflowY: 'hidden', width: '100%', minWidth: 0 }}>
                    <Box display="flex" gridGap={cardGap} style={{ minWidth: rowWidth, width: 'max-content', flexWrap: 'nowrap' }}>
                        {cards.map((card, idx) => (
                            <Box
                                key={`order-card-${idx}`}
                                style={{
                                    width: cardWidth,
                                    minWidth: cardWidth,
                                    flex: `0 0 ${cardWidth}px`,
                                    padding: 4,
                                    boxSizing: 'border-box'
                                }}
                            >
                                <Typography variant="body2" style={{ padding: '2px 0 4px' }}>
                                    品番:{card.hinban}
                                </Typography>
                                <div
                                    className="ag-theme-balham md-order-grid"
                                    style={{
                                        width: '100%',
                                        ...GRID_THEME_STYLE
                                    }}
                                >
                                    <AgGridReact
                                        theme={themeBalham}
                                        columnMenu="legacy"
                                        popupParent={popupParent || undefined}
                                        getRowId={params => params.data.rowid}
                                        rowData={orderGridRows[idx]}
                                        columnDefs={columnDefs}
                                        rowHeight={30}
                                        headerHeight={30}
                                        defaultColDef={defaultColDef}
                                        enableBrowserTooltips={true}
                                        tooltipShowDelay={0}
                                        enableRangeSelection={true}
                                        suppressMultiRangeSelection={true}
                                        suppressContextMenu={true}
                                        suppressCellFocus={true}
                                        suppressRowClickSelection={true}
                                        suppressMovableColumns={true}
                                        suppressDragLeaveHidesColumns={true}
                                        suppressColumnMoveAnimation={true}
                                        rowSelection={'single'}
                                        rowDragManaged={true}
                                        animateRows={true}
                                        domLayout={'autoHeight'}
                                        localeText={GRID_LOCALE_TEXT}
                                    />
                                </div>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}

// 納品予定タブ本体。列ヘッダーと同幅で3つの日付ヘッダーを固定表示する。
function RenderDeliveryTab({ deliveryData, themeStyle, popupParent }) {
    const cardGap = 8;
    const deliveryVisibleCards = 2;
    const deliveryScrollRef = useRef(null);
    const deliveryViewportWidth = useObservedElementWidth(deliveryScrollRef);
    const deliveryHeaderStyle = useMemo(() => ({
        backgroundColor: themeStyle && themeStyle.header ? themeStyle.header.back : '#93A5A1',
        color: themeStyle && themeStyle.header ? themeStyle.header.color : '#FFFFFF'
    }), [themeStyle]);
    const defaultColDef = useMemo(() => ({
        editable: false,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        menuTabs: ['generalMenuTab'],
        suppressMenu: true,
        filter: false,
        flex: 0,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        headerStyle: deliveryHeaderStyle
    }), [deliveryHeaderStyle]);
    const blocks = useMemo(() => normalizeDeliveryBlocks(deliveryData), [deliveryData]);
    const deliveryGridRows = useMemo(() => blocks.map((block) => toDeliveryGridRows(block)), [blocks]);
    const deliveryLabelWidth = 110;
    const baseColumnWidths = useMemo(() => buildDeliveryColumnWidths(1), []);
    const baseGridWidth = useMemo(
        () => baseColumnWidths.grip + baseColumnWidths.color + baseColumnWidths.size + baseColumnWidths.inputQty + baseColumnWidths.actualQty + baseColumnWidths.planQty + baseColumnWidths.centerQty + baseColumnWidths.remainQty,
        [baseColumnWidths]
    );
    const cardWidth = useMemo(() => {
        if (deliveryViewportWidth <= 0) return baseGridWidth;
        const gapTotal = cardGap * Math.max(deliveryVisibleCards - 1, 0);
        const raw = Math.floor((deliveryViewportWidth - gapTotal) / deliveryVisibleCards);
        return Math.max(baseGridWidth, raw);
    }, [deliveryViewportWidth, baseGridWidth]);
    const deliveryGridScale = useMemo(() => cardWidth / baseGridWidth, [cardWidth, baseGridWidth]);
    const columnDefs = useMemo(() => buildDeliveryColumnDefs(deliveryGridScale), [deliveryGridScale]);
    const columnWidths = useMemo(() => buildDeliveryColumnWidths(deliveryGridScale), [deliveryGridScale]);
    const dateHeaderOffsetWidth = useMemo(
        () => columnWidths.grip + columnWidths.color + columnWidths.size + columnWidths.inputQty,
        [columnWidths]
    );
    const gridWidth = useMemo(
        () => columnWidths.grip + columnWidths.color + columnWidths.size + columnWidths.inputQty + columnWidths.actualQty + columnWidths.planQty + columnWidths.centerQty + columnWidths.remainQty,
        [columnWidths]
    );
    const rowWidth = Math.max(
        deliveryViewportWidth || cardWidth,
        (blocks.length * cardWidth) + (Math.max(blocks.length - 1, 0) * cardGap)
    );
    const showHorizontalScroll = deliveryViewportWidth > 0 && (rowWidth > (deliveryViewportWidth + 1));

    return (
        <Paper square variant="outlined" style={{ borderColor: COLORS.line, backgroundColor: 'transparent', borderRadius: 0, padding: 6, minWidth: 0 }}>
            <style>
                {`
                .md-delivery-grid.ag-theme-balham .ag-layout-auto-height .ag-center-cols-viewport,
                .md-delivery-grid.ag-theme-balham .ag-layout-auto-height .ag-center-cols-container,
                .md-delivery-grid.ag-theme-balham .ag-layout-auto-height .ag-center-cols-clipper {
                    min-height: unset !important;
                }
                .md-delivery-grid.ag-theme-balham .ag-header-cell-label {
                    justify-content: center;
                }
                .md-delivery-grid.ag-theme-balham .ag-header-cell-text {
                    text-align: center;
                    width: 100%;
                }
                .md-delivery-grid.ag-theme-balham .ag-body-horizontal-scroll,
                .md-delivery-grid.ag-theme-balham .ag-body-horizontal-scroll-viewport,
                .md-delivery-grid.ag-theme-balham .ag-body-horizontal-scroll-container {
                    height: 0 !important;
                    min-height: 0 !important;
                    max-height: 0 !important;
                    overflow: hidden !important;
                }
                `}
            </style>
            <Box style={{ padding: 6 }}>
                <Box ref={deliveryScrollRef} style={{ overflowX: showHorizontalScroll ? 'auto' : 'hidden', overflowY: 'hidden', width: '100%', minWidth: 0 }}>
                    <Box display="flex" gridGap={cardGap} style={{ minWidth: rowWidth, width: 'max-content', flexWrap: 'nowrap' }}>
                        {blocks.map((block, idx) => (
                            <Box key={`delivery-block-${idx}`} style={{ width: cardWidth, minWidth: cardWidth, flex: `0 0 ${cardWidth}px`, padding: 4 }}>
                                <Box display="flex" justifyContent="flex-start" alignItems="flex-end" marginBottom={0}>
                                    <Box>
                                        <Box display="flex" alignItems="center">
                                            <Typography variant="body2" style={{ width: deliveryLabelWidth, minWidth: deliveryLabelWidth, whiteSpace: 'nowrap', textAlign: 'right' }}>品番:</Typography>
                                            <Typography variant="body2" style={{ marginLeft: 4 }}>{block.hinban}</Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center">
                                            <Typography variant="body2" style={{ width: deliveryLabelWidth, minWidth: deliveryLabelWidth, whiteSpace: 'nowrap', textAlign: 'right' }}>加工指示番号:</Typography>
                                            <Typography variant="body2" style={{ marginLeft: 4 }}>{block.processNo}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box display="flex" style={{ marginTop: 2, marginBottom: 2, width: gridWidth, minWidth: gridWidth }}>
                                    <Box style={{ width: dateHeaderOffsetWidth, minWidth: dateHeaderOffsetWidth }} />
                                    <Box style={deliveryDateHeaderCellStyle(columnWidths.actualQty)}>
                                        {deliveryDateHeaderCell(block.actualDate)}
                                    </Box>
                                    <Box style={deliveryDateHeaderCellStyle(columnWidths.planQty)}>
                                        {deliveryDateHeaderCell(block.planDate)}
                                    </Box>
                                    <Box style={deliveryDateHeaderCellStyle(columnWidths.centerQty)}>
                                        {deliveryDateHeaderCell(block.centerDate)}
                                    </Box>
                                    <Box style={{ width: columnWidths.remainQty, minWidth: columnWidths.remainQty }} />
                                </Box>

                                <div
                                    className="ag-theme-balham md-delivery-grid"
                                    style={{
                                        width: gridWidth,
                                        minWidth: gridWidth,
                                        ...GRID_THEME_STYLE
                                    }}
                                >
                                    <AgGridReact
                                        theme={themeBalham}
                                        columnMenu="legacy"
                                        popupParent={popupParent || undefined}
                                        getRowId={params => params.data.rowid}
                                        rowData={deliveryGridRows[idx]}
                                        columnDefs={columnDefs}
                                        rowHeight={30}
                                        headerHeight={30}
                                        defaultColDef={defaultColDef}
                                        enableBrowserTooltips={true}
                                        tooltipShowDelay={0}
                                        enableRangeSelection={true}
                                        suppressMultiRangeSelection={true}
                                        suppressContextMenu={true}
                                        suppressCellFocus={true}
                                        suppressRowClickSelection={true}
                                        suppressDragLeaveHidesColumns={true}
                                        suppressMovableColumns={true}
                                        suppressHorizontalScroll={true}
                                        rowSelection={'single'}
                                        rowDragManaged={true}
                                        animateRows={true}
                                        domLayout={'autoHeight'}
                                        localeText={GRID_LOCALE_TEXT}
                                    />
                                </div>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}

// デバッグ/フォールバック用の単純な項目一覧表示。
function RenderSimpleTab({ data, fields }) {
    return (
        <Table size="small" style={{ backgroundColor: '#fff' }}>
            <TableHead>
                <TableRow>
                    <TableCell style={{ width: '35%', fontWeight: 700 }}>項目</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>値</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {fields.map((field) => (
                    <TableRow key={field.key}>
                        <TableCell>{field.label}</TableCell>
                        <TableCell>{valueOf(data, field.key)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

// MD投入表の基本情報セクション。投入数/受注数/納品予定の3タブを表示する。
function MDtounyuhyo(props) {
    const rootRef = useRef(null);
    const [localActiveTab, setlocalActiveTab] = useState('input');
    const activeTab = props.activeTab || localActiveTab;
    const changeTab = props.onChangeTab || setlocalActiveTab;
    const themestyle = props.themeStyle;
    const data = resolveData(props.tabBaseInfo);
    const headBg = themestyle && themestyle.header ? themestyle.header.back : '#8ea0a0';
    const headFg = themestyle && themestyle.header ? themestyle.header.color : '#fff';
    const btnBg = '#4caf50';
    const panelBg = themestyle && themestyle.body ? themestyle.body.back : '#efefef';
    const tabInactiveBg = '#d9d9d9';
    const tabInactiveText = '#666';
    const isBuiltInTab = TAB_ITEMS.some((tab) => tab.value === activeTab);

    return (
        <Box
            ref={rootRef}
            style={{
                padding: 8,
                '--md-panel-bg': panelBg,
                '--md-head-bg': headBg,
                '--md-head-fg': headFg,
                '--md-btn-bg': btnBg
            }}
        >
            <Box
                style={{
                    paddingBottom: 8
                }}
            >
                <div style={{ marginBottom: 8 }}>基本情報</div>
                <Box style={{ display: 'inline-flex', backgroundColor: tabInactiveBg, borderRadius: 16, padding: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(event, value) => {
                            void event;
                            changeTab(value);
                        }}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        style={{ minHeight: 0 }}
                    >
                        {TAB_ITEMS.map((tab) => {
                            const selected = activeTab === tab.value;
                            return (
                                <Tab
                                    key={tab.value}
                                    value={tab.value}
                                    label={tab.label}
                                    disableRipple
                                    style={{
                                        minHeight: 24,
                                        minWidth: 78,
                                        padding: '0 16px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderRadius: 14,
                                        backgroundColor: selected ? COLORS.btn : 'transparent',
                                        color: selected ? '#fff' : tabInactiveText,
                                        opacity: 1
                                    }}
                                />
                            );
                        })}
                    </Tabs>
                </Box>
            </Box>

            {activeTab === 'input' ? (
                <RenderInputTab
                    inputData={data.input}
                    fnc={props.fnc}
                    jutakuHinFlg={props.jutakuHinFlg}
                    onAddExists={props.onAddExists}
                    onAddNew={props.onAddNew}
                    onOrder={props.onProductOrder}
                    onSubMaterial={props.onSubMaterial}
                    onUnlink={props.onUnlink}
                    onToggleStop={props.onToggleStop}
                    onChangeBlocks={props.onInputBlocksChange}
                    lockIssuedData={props.lockIssuedData}
                    commandsDisabled={props.commandsDisabled}
                />
            ) : null}
            {activeTab === 'order' ? (
                <RenderOrderTab
                    orderData={data.order}
                    onApplyInput={props.onApplyInput}
                    themeStyle={themestyle}
                    popupParent={rootRef.current}
                />
            ) : null}
            {activeTab === 'delivery' ? (
                <RenderDeliveryTab deliveryData={data.delivery} themeStyle={themestyle} popupParent={rootRef.current} />
            ) : null}
            {!isBuiltInTab ? (
                <RenderSimpleTab data={data[activeTab]} fields={TAB_FIELDS[activeTab] || []} />
            ) : null}
        </Box>
    );
}

function mdTounyuhyoPropsEqual(prev, next) {
    // 親画面のメニュー系stateが変わっても、基本情報セクションに必要なpropsが同一なら再描画しない。
    const keys = [
        'tabBaseInfo',
        'themeStyle',
        'fnc',
        'jutakuHinFlg',
        'activeTab',
        'onChangeTab',
        'onAddExists',
        'onAddNew',
        'onProductOrder',
        'onSubMaterial',
        'onUnlink',
        'onToggleStop',
        'onInputBlocksChange',
        'onApplyInput',
        'lockIssuedData',
        'commandsDisabled'
    ];
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (prev[key] !== next[key]) {
            return false;
        }
    }
    return true;
}

export default React.memo(MDtounyuhyo, mdTounyuhyoPropsEqual);






